import { apiSlice } from './apiSlice';

// Path segments are typed `number | string`, so an id carrying '/', '?', '#' or
// '../' would escape the endpoint (path traversal / route tampering).
// encodeURIComponent is a no-op for numeric ids.
const segment = (id: number | string): string => encodeURIComponent(String(id));

export const auditAndReconciliationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Audit Trail
    getAuditTrail: builder.query<any, Record<string, any>>({
      query: (params) => ({
        url: '/api/accounting/audit-trail',
        params,
      }),
      providesTags: ['AuditTrail'],
    }),

    // Bank Reconciliation
    getBankReconciliations: builder.query<any, Record<string, any>>({
      query: (params) => ({
        url: '/api/bank-reconciliations',
        params,
      }),
      providesTags: ['BankReconciliation'],
    }),
    getBankReconciliationById: builder.query<any, number | string>({
      query: (id) => ({ url: `/api/bank-reconciliations/${segment(id)}` }),
      providesTags: (result, error, id) => [{ type: 'BankReconciliation', id }],
    }),
    createBankReconciliation: builder.mutation<any, any>({
      query: (data) => ({
        url: '/api/bank-reconciliations',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['BankReconciliation'],
    }),
    toggleBankReconciliationLine: builder.mutation<any, { reconciliationId: number | string; lineId: number | string; isCleared: boolean }>({
      query: ({ reconciliationId, lineId, isCleared }) => ({
        url: `/api/bank-reconciliations/${segment(reconciliationId)}/lines/${segment(lineId)}/toggle`,
        method: 'POST',
        data: { isCleared },
      }),
      invalidatesTags: (result, error, { reconciliationId }) => [{ type: 'BankReconciliation', id: reconciliationId }],
    }),
    completeBankReconciliation: builder.mutation<any, number | string>({
      query: (id) => ({
        url: `/api/bank-reconciliations/${segment(id)}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'BankReconciliation', id }, 'BankReconciliation'],
    }),
    deleteBankReconciliation: builder.mutation<void, number | string>({
      query: (id) => ({
        url: `/api/bank-reconciliations/${segment(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BankReconciliation'],
    }),
    importStatement: builder.mutation<any, { id: number | string; data: FormData }>({
      query: ({ id, data }) => ({
        url: `/api/bank-reconciliations/${segment(id)}/import`,
        method: 'POST',
        data: data,
      }),
      // `getStatementLines` provides `{type:'BankReconciliation', id:
      // 'statement-<id>'}` — an import rewrites exactly those lines, so the
      // reconciliation/detail/list tags alone would leave the statement list
      // stale after a successful import.
      invalidatesTags: (result, error, { id }) => [
        { type: 'BankReconciliation', id },
        { type: 'BankReconciliation', id: `statement-${id}` },
        'BankReconciliation',
      ],
    }),
    getStatementLines: builder.query<any, number | string>({
      query: (id) => ({ url: `/api/bank-reconciliations/${segment(id)}/statements` }),
      providesTags: (result, error, id) => [{ type: 'BankReconciliation', id: `statement-${id}` }],
    }),

    // Control Account Reconciliation
    getControlAccountReconciliation: builder.query<any, Record<string, any>>({
      query: (params) => ({
        url: '/api/accounting/reconciliation/control-accounts',
        params,
      }),
      providesTags: ['ControlAccountReconciliation'],
    }),
    runControlAccountReconciliation: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/api/accounting/reconciliation/control-accounts/run',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['ControlAccountReconciliation'],
    }),
    getOperationalVsGL: builder.query<any, Record<string, any>>({
      query: (params) => ({
        url: '/api/accounting/reconciliation/operational-vs-gl',
        params,
      }),
    }),
    verifyAuditTrailChain: builder.mutation<any, Record<string, any> | void>({
      query: (data) => ({
        url: '/api/accounting/audit-chain/verify',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AuditTrail'],
    }),
  }),
});

export const {
  useGetAuditTrailQuery,
  useVerifyAuditTrailChainMutation: useVerifyAuditChainMutation,
  useGetBankReconciliationsQuery,
  useGetBankReconciliationByIdQuery,
  useLazyGetBankReconciliationByIdQuery,
  useCreateBankReconciliationMutation,
  useToggleBankReconciliationLineMutation,
  useCompleteBankReconciliationMutation,
  useDeleteBankReconciliationMutation,
  useImportStatementMutation,
  useGetStatementLinesQuery,
  useLazyGetStatementLinesQuery,
  useGetControlAccountReconciliationQuery,
  useRunControlAccountReconciliationMutation,
  useGetOperationalVsGLQuery,
} = auditAndReconciliationApi;
